import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowLeftRight, ArrowRight, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Download, Eye, EyeOff,
  Grid3x3, Lock, Route, Calculator, Upload, AlertTriangle, CheckCircle2, CircleDashed, Pencil, Plus,
  Trash2, Save, X, Paperclip, Scissors, Merge, Languages, LayoutDashboard, Scale, Repeat, Presentation, FileText,
} from 'lucide-react';
import Shell from '../components/TopBarShell';
import { useLocale } from '../contexts/LocaleProvider';
import { LT } from '../contexts/AppCatalogueContext';
import api from '../utils/api';
import { CONTENT_LOCALES, coverage, ensureTree, localize } from '../lib/careerMapI18n';
import { COPY, tr } from '../components/careermap/copy';
import { cellFor, stageFor, STAGE_TONES, toneFor, compIcon, keywordRow } from '../components/careermap/model';
import { CellText, Tagline, Competency } from '../components/careermap/shared';
import { NEXT_RE, rowIcon, rowIndex } from '../components/careermap/model';
import Overview from '../components/careermap/Overview';
import LevelExplorer from '../components/careermap/LevelExplorer';
import RulesView from '../components/careermap/RulesView';

/* Career map · HR's career grid (general + job annexes), explorable by
 * everyone and fully editable by HR in the page itself.
 *
 * The content is confidential until HR publishes it, so none of it lives in
 * this file: it sits in the database, and the API only returns unpublished
 * maps to HR and admins. The Excel HR worked from is a reference attachment.
 * The simulator's thresholds are part of each map's data (`promotion`), so
 * HR edits them here too.
 *
 * The map content is in French (as HR writes it); the page chrome follows
 * the portal language. */

const clone = (x) => JSON.parse(JSON.stringify(x));
const blankCells = (levels) => levels.map(l => ({ levels: [l.n], text: '' }));
const inputCls = 'rounded-lg px-2.5 py-1.5 text-[13.5px] w-full min-w-0 outline-none focus:ring-2';
const inputStyle = { background: '#fff', border: `1px solid ${LT.green30}`, color: LT.greenDeep, '--tw-ring-color': LT.greenMid };

/* A textarea that grows with its text · used for every editable block. */
function Area({ value, onChange, rows = 3, placeholder, id }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (el) { el.style.height = 'auto'; el.style.height = `${el.scrollHeight + 2}px`; }
  }, [value]);
  return (
    <textarea ref={ref} id={id} value={value || ''} rows={rows} placeholder={placeholder}
              onChange={e => onChange(e.target.value)}
              className={`${inputCls} leading-relaxed resize-y`} style={inputStyle} />
  );
}

function Field({ value, onChange, placeholder, className = '', id }) {
  return <input id={id} type="text" value={value ?? ''} placeholder={placeholder} onChange={e => onChange(e.target.value)}
                className={`${inputCls} ${className}`} style={inputStyle} />;
}

function IconBtn({ onClick, label, children, danger, disabled }) {
  return (
    <button type="button" onClick={onClick} aria-label={label} title={label} disabled={disabled}
            className="h-7 w-7 rounded-md grid place-items-center transition hover:bg-black/[0.05] disabled:opacity-30"
            style={{ color: danger ? '#b23a2a' : LT.green50 }}>
      {children}
    </button>
  );
}

const VIEWS = [
  ['overview', LayoutDashboard], ['levels', Route], ['compare', ArrowLeftRight],
  ['prepare', Calculator], ['rules', Scale], ['grid', Grid3x3],
];

/* ── Page ─────────────────────────────────────────────────────────── */

export default function CareerMap() {
  const { locale } = useLocale();
  const [state, setState] = useState(null);
  const [error, setError] = useState(false);
  // Map, view and level live in the URL, so any screen can be shared:
  // /career-map?map=support&view=levels&n=4
  const [params, setParams] = useSearchParams();
  const view = VIEWS.some(([k]) => k === params.get('view')) ? params.get('view') : 'overview';
  const level = Math.max(1, Number(params.get('n')) || 1);
  const go = (patch) => setParams(prev => {
    const next = new URLSearchParams(prev);
    Object.entries(patch).forEach(([k, v]) => (v == null ? next.delete(k) : next.set(k, String(v))));
    return next;
  }, { replace: false });
  const active = params.get('map');
  const setActive = (fnOrSlug) => setParams(prev => {
    const next = new URLSearchParams(prev);
    const slug = typeof fnOrSlug === 'function' ? fnOrSlug(prev.get('map')) : fnOrSlug;
    if (slug) next.set('map', slug); else next.delete('map');
    return next;
  }, { replace: true });
  const [draft, setDraft] = useState(null);       // editable copy of the active map's data
  const [saveState, setSaveState] = useState(''); // '', 'saving', 'failed'

  const load = (keep) => api.careerMaps().then(d => {
    setState(d);
    setActive(a => {
      const want = keep || a;
      return d.maps.some(m => m.slug === want) ? want : d.maps[0]?.slug || null;
    });
  }).catch(() => setError(true));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const [editLang, setEditLang] = useState('fr');  // language of the texts being edited

  const map = state?.maps.find(m => m.slug === active);
  const editing = draft != null;
  // French is canonical · `canon` drives structure and logic, `data` is
  // what the page shows (and, while editing, the language being edited).
  const canon = editing ? draft : map?.data;
  const data = editing
    ? (editLang === 'fr' ? draft : draft.i18n?.[editLang])
    : localize(map?.data, locale);
  const dirty = editing && map && JSON.stringify(draft) !== JSON.stringify(map.data);

  // Three kinds of edits on the draft:
  //   text   · a wording, in the language being edited
  //   update · structure (rows, cells, criteria), applied to every language
  //            so translations stay aligned path for path
  //   root   · French-only settings (the simulator's thresholds)
  const mutate = (fn) => setDraft(d => { const next = clone(d); fn(next); return next; });
  const text = (fn) => mutate(d => fn(editLang === 'fr' ? d : ensureTree(d, editLang)));
  const update = (fn) => mutate(d => { fn(d); Object.values(d.i18n || {}).forEach(t => fn(t)); });
  const root = (fn) => mutate(fn);
  const pickEditLang = (lang) => {
    if (lang !== 'fr') mutate(d => { ensureTree(d, lang); });
    setEditLang(lang);
  };

  const switchMap = (slug) => {
    if (dirty) return;  // the save bar stays up until the edits are saved or discarded
    setDraft(null);
    setActive(slug);
  };
  const save = async () => {
    setSaveState('saving');
    try {
      await api.updateCareerMap(map.slug, { data: draft });
      await load(map.slug);
      setDraft(null);
      setSaveState('');
    } catch {
      setSaveState('failed');
    }
  };

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  return (
    <Shell>
      <div className="max-w-[1180px] mx-auto pb-24">
        {error && <p className="mt-8 text-[14px]" style={{ color: '#b23a2a' }}>{tr(COPY.failed, locale)}</p>}
        {!state && !error && <p className="mt-8 text-[14px]" style={{ color: LT.green50 }}>…</p>}

        {state && !state.maps.length && !state.can_manage && (
          <p className="mt-10 text-[15px]" style={{ color: LT.green50 }}>{tr(COPY.empty, locale)}</p>
        )}

        {state && (state.maps.length > 0 || state.can_manage) && (
          <>
            <header className="max-w-[760px]">
              <div className="text-[11px] uppercase tracking-[0.16em] font-medium" style={{ color: LT.green50 }}>{tr(COPY.eyebrow, locale)}</div>
              <h1 className="font-display text-[2.2rem] sm:text-[2.6rem] leading-tight tracking-[-0.02em] mt-1" style={{ color: LT.greenDeep }}>
                {tr(COPY.title, locale)}
              </h1>
              <p className="text-[15px] leading-relaxed mt-2" style={{ color: '#3a4f44' }}>{tr(COPY.lede, locale)}</p>
            </header>

            <Documents locale={locale} docs={state.documents || []} canManage={state.can_manage} onReload={() => load(active)} />

            {state.can_manage && (
              <ManageBar locale={locale} map={map} editing={editing}
                         onEdit={() => { setDraft(clone(map.data)); setEditLang('fr'); }}
                         editLang={editLang} onEditLang={pickEditLang}
                         onReload={load} />
            )}

            {state.maps.length === 0 && (
              <p className="mt-8 text-[14px]" style={{ color: LT.green50 }}>{tr(COPY.emptyHr, locale)}</p>
            )}

            {map && data && canon && (
              <>
                <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
                  {state.maps.length > 1 ? (
                    <div className="inline-flex p-1 rounded-xl flex-wrap" style={{ background: LT.beigeWarm }} role="tablist">
                      {state.maps.map(m => (
                        <button key={m.slug} type="button" role="tab" aria-selected={m.slug === active}
                                onClick={() => switchMap(m.slug)} disabled={dirty && m.slug !== active}
                                className="px-4 py-2 rounded-lg text-[14px] transition inline-flex items-center gap-1.5 disabled:opacity-40"
                                style={m.slug === active
                                  ? { background: '#fff', color: LT.greenDeep, boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }
                                  : { color: LT.green50 }}>
                          {!m.is_published && <Lock className="h-3 w-3" aria-label="draft" />}
                          {COPY.mapNames[m.slug] ? tr(COPY.mapNames[m.slug], locale) : m.title}
                        </button>
                      ))}
                    </div>
                  ) : <span />}
                </div>

                {/* One row of views · underline tabs, scroll sideways on a phone */}
                <nav className="mt-5 -mx-1 px-1 overflow-x-auto border-b" style={{ borderColor: 'rgba(43,65,56,0.12)' }} aria-label={tr(COPY.title, locale)}>
                  <div className="flex gap-1 min-w-max" role="tablist">
                    {VIEWS.map(([key, Icon]) => (
                      <button key={key} type="button" role="tab" aria-selected={view === key} onClick={() => go({ view: key })}
                              className="px-3 py-2.5 text-[14px] inline-flex items-center gap-2 -mb-px border-b-2 transition whitespace-nowrap"
                              style={view === key
                                ? { color: LT.greenDeep, borderColor: LT.yellow }
                                : { color: LT.green50, borderColor: 'transparent' }}>
                        <Icon className="h-4 w-4" /> {tr(COPY.nav[key], locale)}
                      </button>
                    ))}
                  </div>
                </nav>

                {editing && (
                  <div className="mt-3 max-w-[900px]"><Field value={data?.subtitle} onChange={v => text(d => { d.subtitle = v; })} /></div>
                )}

                {editing ? (
                  <>
                    {(view === 'overview' || view === 'levels') && <PathView key={map.slug} map={data} locale={locale} editing update={update} text={text} keywordRow={keywordRow(canon)} />}
                    {view === 'compare' && <CompareView key={map.slug} map={data} canon={canon} locale={locale} keywordRow={keywordRow(canon)} />}
                    {view === 'grid' && <GridView map={data} canon={canon} locale={locale} editing update={update} text={text} />}
                    {view === 'prepare' && <Simulator key={map.slug} map={data} canon={canon} locale={locale} editing update={update} text={text} root={root} />}
                    {view === 'rules' && <Reference map={data} canon={canon} locale={locale} editing update={update} text={text} />}
                  </>
                ) : (
                  <>
                    {view === 'overview' && (
                      <Overview map={data} canon={canon} locale={locale} keywordRow={keywordRow(canon)}
                                onOpenLevel={(n) => go({ view: 'levels', n })} />
                    )}
                    {view === 'levels' && (
                      <LevelExplorer map={data} canon={canon} locale={locale} keywordRow={keywordRow(canon)}
                                     level={Math.min(level, data.levels.length)} onLevel={(n) => go({ n })}
                                     onSimulate={(n) => go({ view: 'prepare', n })} />
                    )}
                    {view === 'compare' && <CompareView key={map.slug} map={data} canon={canon} locale={locale} keywordRow={keywordRow(canon)} initialLevel={level} />}
                    {view === 'prepare' && <Simulator key={`${map.slug}-${level}`} map={data} canon={canon} locale={locale} initialLevel={level} />}
                    {view === 'rules' && <RulesView map={data} canon={canon} locale={locale} />}
                    {view === 'grid' && <GridView map={data} canon={canon} locale={locale} />}
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>

      {editing && (
        <div className="fixed left-0 right-0 bottom-0 z-40 px-4"
             style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}>
          <div className="max-w-[1180px] mx-auto rounded-2xl px-4 py-3 flex flex-wrap items-center gap-3 shadow-lg"
               style={{ background: LT.greenDeep, color: LT.beigeLight }}>
            <Pencil className="h-4 w-4" />
            <span className="text-[14px] flex-1 min-w-0">
              {saveState === 'failed' ? tr(COPY.saveFailed, locale) : dirty ? tr(COPY.unsaved, locale) : map?.title}
            </span>
            <button type="button" onClick={() => { setDraft(null); setSaveState(''); }}
                    className="inline-flex items-center gap-1.5 text-[13.5px] px-3 py-2 rounded-lg"
                    style={{ border: '1px solid rgba(255,255,255,0.3)' }}>
              <X className="h-3.5 w-3.5" /> {tr(dirty ? COPY.discard : COPY.cancel, locale)}
            </button>
            <button type="button" onClick={save} disabled={!dirty || saveState === 'saving'}
                    className="inline-flex items-center gap-1.5 text-[13.5px] px-4 py-2 rounded-lg font-medium disabled:opacity-50"
                    style={{ background: LT.yellow, color: LT.greenDeep }}>
              <Save className="h-3.5 w-3.5" /> {tr(saveState === 'saving' ? COPY.saving : COPY.save, locale)}
            </button>
          </div>
        </div>
      )}
      <style>{`
        .cm-btn { display:inline-flex; align-items:center; gap:6px; font-size:13px; padding:6px 11px; border-radius:8px;
                  background:#fff; color:${LT.greenDeep}; border:1px solid ${LT.green30}; }
        .cm-btn:hover { border-color:${LT.greenMid}; }
        .cm-btn-primary { background:${LT.greenDeep}; color:${LT.beigeLight}; border-color:${LT.greenDeep}; }
      `}</style>
    </Shell>
  );
}

/* ── Documents · files that go with the career map (the team deck…) ── */

function Documents({ locale, docs, canManage, onReload }) {
  const [busy, setBusy] = useState(false);
  const addRef = useRef(null);
  if (!docs.length && !canManage) return null;
  const size = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} Mo` : `${Math.max(1, Math.round(b / 1024))} Ko`).replace('.', locale === 'en' ? '.' : ',');
  const ext = (name) => (name.split('.').pop() || '').toUpperCase();
  const download = async (d) => {
    const r = await api.careerMapDocument(d.id);
    const url = URL.createObjectURL(r.data);
    Object.assign(document.createElement('a'), { href: url, download: d.file_name }).click();
    URL.revokeObjectURL(url);
  };
  const run = async (fn) => { setBusy(true); try { await fn(); await onReload(); } finally { setBusy(false); } };

  return (
    <section className="mt-5" aria-label={tr(COPY.documents, locale)}>
      <div className="flex flex-wrap items-stretch gap-2">
        {docs.map(d => {
          const Icon = /pptx?|key/i.test(ext(d.file_name)) ? Presentation : FileText;
          return (
            <div key={d.id} className="flex items-center gap-3 pl-3 pr-1.5 py-1.5 min-w-0"
                 style={{ background: LT.beigeLight, border: '1px solid rgba(43,65,56,0.12)' }}>
              <span className="h-9 w-9 shrink-0 rounded-full grid place-items-center" style={{ background: LT.yellow }}>
                <Icon className="h-4 w-4" style={{ color: LT.greenDeep }} />
              </span>
              <span className="min-w-0">
                <span className="block text-[14px] truncate" style={{ color: LT.greenDeep }}>{d.title}</span>
                <span className="block text-[11.5px] font-num" style={{ color: LT.green50 }}>{ext(d.file_name)} · {size(d.size)}</span>
              </span>
              <button type="button" onClick={() => download(d)}
                      className="ml-2 inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[13.5px] transition hover:opacity-90"
                      style={{ background: LT.cta, color: '#fff' }}>
                <Download className="h-3.5 w-3.5" /> {tr(COPY.download, locale)}
              </button>
              {canManage && (
                <IconBtn label={tr(COPY.removeDoc, locale)} danger disabled={busy}
                         onClick={() => run(() => api.deleteCareerMapDocument(d.id))}><Trash2 className="h-3.5 w-3.5" /></IconBtn>
              )}
            </div>
          );
        })}
        {canManage && (
          <>
            <button type="button" disabled={busy} onClick={() => addRef.current?.click()}
                    className="inline-flex items-center gap-1.5 px-4 text-[13px] disabled:opacity-50"
                    style={{ border: '1px dashed rgba(43,65,56,0.3)', color: LT.greenDeep, minHeight: 52 }}>
              <Plus className="h-3.5 w-3.5" /> {tr(COPY.addDoc, locale)}
            </button>
            <input ref={addRef} type="file" hidden
                   onChange={e => { const f = e.target.files[0]; e.target.value = ''; if (f) run(() => api.addCareerMapDocument(f)); }} />
          </>
        )}
      </div>
    </section>
  );
}

/* ── HR bar: draft state, edit, publish, attachment, new map ──────── */

function ManageBar({ locale, map, editing, onEdit, editLang, onEditLang, onReload }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [err, setErr] = useState('');
  const [confirm, setConfirm] = useState('');   // 'publish' | 'delete' | ''
  const [name, setName] = useState('');
  const [title, setTitle] = useState(map?.title || '');
  const [alsoImport, setAlsoImport] = useState(false);
  const attachRef = useRef(null);
  const createRef = useRef(null);

  useEffect(() => { setTitle(map?.title || ''); setConfirm(''); setAlsoImport(false); }, [map?.slug, map?.title]);

  const run = async (fn, keep) => {
    setBusy(true); setErr('');
    try {
      const out = await fn();
      await onReload(keep || out?.slug);
      return true;
    } catch (e) {
      setErr(e?.response?.data?.detail || tr(COPY.saveFailed, locale));
      return false;
    } finally {
      setBusy(false); setConfirm('');
    }
  };
  const download = async () => {
    const r = await api.careerMapFile(map.slug);
    const url = URL.createObjectURL(r.data);
    Object.assign(document.createElement('a'), { href: url, download: map.attachment_name || `${map.slug}.xlsx` }).click();
    URL.revokeObjectURL(url);
  };
  // Translate NL then EN, a few batches per call, until nothing is left.
  const translateAll = async (reset) => {
    setBusy(true); setErr('');
    try {
      for (const loc of ['nl', 'en']) {
        let first = true;
        let left = null;
        while (first || left > 0) {
          setProgress(`${loc.toUpperCase()}${left ? ` · ${left}` : ''}`);
          const r = await api.translateCareerMap(map.slug, loc, reset && first);
          if (left !== null && r.remaining >= left) break;  // no progress · stop rather than loop
          left = r.remaining;
          first = false;
        }
      }
      await onReload(map.slug);
    } catch (e) {
      setErr(e?.response?.data?.detail || tr(COPY.translateFailed, locale));
    } finally {
      setBusy(false); setProgress('');
    }
  };
  const create = async (file) => {
    if (await run(() => api.createCareerMap({ title: name.trim(), file }))) setName('');
  };

  return (
    <div className="mt-6 rounded-2xl p-4 space-y-3"
         style={{ background: map?.is_published ? LT.greenSoft : LT.yellowLight, border: `1px solid ${map?.is_published ? LT.green30 : LT.yellow}` }}>
      {map && (
        <>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="inline-flex items-center gap-1.5 text-[13.5px] font-medium" style={{ color: LT.greenDeep }}>
              {map.is_published ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              {tr(map.is_published ? COPY.published : COPY.draft, locale)}
            </span>
            <div className="flex flex-wrap gap-2 ml-auto items-center">
              {!editing && (
                <button type="button" onClick={onEdit} className="cm-btn cm-btn-primary"><Pencil className="h-3.5 w-3.5" /> {tr(COPY.edit, locale)}</button>
              )}
              {map.is_published ? (
                <button type="button" onClick={() => run(() => api.updateCareerMap(map.slug, { is_published: false }), map.slug)} disabled={busy} className="cm-btn">
                  <EyeOff className="h-3.5 w-3.5" /> {tr(COPY.unpublish, locale)}
                </button>
              ) : confirm === 'publish' ? (
                <span className="inline-flex gap-2 items-center text-[13px]" style={{ color: LT.greenDeep }}>
                  {tr(COPY.confirmPub, locale)}
                  <button type="button" onClick={() => run(() => api.updateCareerMap(map.slug, { is_published: true }), map.slug)} className="cm-btn cm-btn-primary">{tr(COPY.yes, locale)}</button>
                  <button type="button" onClick={() => setConfirm('')} className="cm-btn">{tr(COPY.cancel, locale)}</button>
                </span>
              ) : (
                <button type="button" onClick={() => setConfirm('publish')} disabled={busy} className="cm-btn">
                  <Eye className="h-3.5 w-3.5" /> {tr(COPY.publish, locale)}
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-[13px]" style={{ color: '#4d6157' }}>
            <Paperclip className="h-3.5 w-3.5" />
            <span>{tr(COPY.attachment, locale)} :</span>
            {map.attachment_name ? (
              <button type="button" onClick={download} className="underline inline-flex items-center gap-1" style={{ color: LT.greenDeep }}>
                {map.attachment_name} <Download className="h-3 w-3" />
              </button>
            ) : <span>{tr(COPY.noAttach, locale)}</span>}
            <button type="button" onClick={() => attachRef.current?.click()} disabled={busy} className="cm-btn ml-1">
              <Upload className="h-3.5 w-3.5" /> {tr(COPY.attachNew, locale)}
            </button>
            <label className="inline-flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={alsoImport} onChange={e => setAlsoImport(e.target.checked)} />
              {tr(COPY.alsoImport, locale)}
            </label>
            {alsoImport && <span style={{ color: '#9a5b0f' }}>· {tr(COPY.importWarn, locale)}</span>}
            <input ref={attachRef} type="file" accept=".xlsx" hidden
                   onChange={e => { const f = e.target.files[0]; e.target.value = ''; if (f) run(() => api.attachCareerMap(map.slug, f, alsoImport), map.slug); }} />
          </div>

          <div className="flex flex-wrap items-center gap-2 text-[13px]" style={{ color: '#4d6157' }}>
            <Languages className="h-3.5 w-3.5" />
            <span>{tr(COPY.translations, locale)} :</span>
            {['nl', 'en'].map(loc => {
              const pct = Math.round(coverage(map.data, loc) * 100);
              return (
                <span key={loc} className="font-num rounded-full px-2 py-0.5"
                      style={{ background: pct === 100 ? LT.greenSoft : '#fff', border: `1px solid ${LT.green30}`, color: LT.greenDeep }}>
                  {loc.toUpperCase()} {pct} %
                </span>
              );
            })}
            {!editing && (
              <>
                <button type="button" onClick={() => translateAll(false)} disabled={busy} className="cm-btn ml-1">
                  <Languages className="h-3.5 w-3.5" /> {tr(COPY.translate, locale)}
                </button>
                <button type="button" onClick={() => translateAll(true)} disabled={busy} className="cm-btn">
                  {tr(COPY.retranslate, locale)}
                </button>
              </>
            )}
            {progress && <span style={{ color: LT.green50 }}>{tr(COPY.translating, locale)} {progress}…</span>}
            {editing && (
              <span className="inline-flex items-center gap-1 ml-auto">
                <span>{tr(COPY.editIn, locale)}</span>
                <span className="inline-flex p-0.5 rounded-lg" style={{ background: '#fff', border: `1px solid ${LT.green30}` }}>
                  {CONTENT_LOCALES.map(l => (
                    <button key={l} type="button" onClick={() => onEditLang(l)} aria-pressed={editLang === l}
                            className="px-2.5 py-1 rounded-md text-[12.5px] font-medium"
                            style={editLang === l ? { background: LT.greenDeep, color: LT.beigeLight } : { color: LT.greenDeep }}>
                      {l.toUpperCase()}
                    </button>
                  ))}
                </span>
              </span>
            )}
          </div>
          {editing && editLang !== 'fr' && (
            <p className="text-[12.5px]" style={{ color: '#4d6157' }}>{tr(COPY.editInNote, locale)}</p>
          )}

          {editing && (
            <div className="flex flex-wrap items-center gap-2 text-[13px]" style={{ color: '#4d6157' }}>
              <label htmlFor="cm-title">{tr(COPY.rename, locale)} :</label>
              <div className="w-56 max-w-full"><Field id="cm-title" value={title} onChange={setTitle} /></div>
              <button type="button" disabled={!title.trim() || title === map.title || busy} className="cm-btn disabled:opacity-50"
                      onClick={() => run(() => api.updateCareerMap(map.slug, { title }), map.slug)}>{tr(COPY.save, locale)}</button>
              <span className="ml-auto" />
              {confirm === 'delete' ? (
                <span className="inline-flex gap-2 items-center" style={{ color: '#b23a2a' }}>
                  {tr(COPY.confirmDel, locale)}
                  <button type="button" onClick={() => run(async () => { await api.deleteCareerMap(map.slug); window.location.reload(); })} className="cm-btn" style={{ color: '#b23a2a' }}>{tr(COPY.yes, locale)}</button>
                  <button type="button" onClick={() => setConfirm('')} className="cm-btn">{tr(COPY.cancel, locale)}</button>
                </span>
              ) : (
                <button type="button" onClick={() => setConfirm('delete')} className="cm-btn" style={{ color: '#b23a2a' }}>
                  <Trash2 className="h-3.5 w-3.5" /> {tr(COPY.deleteMap, locale)}
                </button>
              )}
            </div>
          )}
        </>
      )}

      {!editing && (
        <div className={`flex flex-wrap items-center gap-2 ${map ? 'pt-3 border-t' : ''}`} style={{ borderColor: 'rgba(43,65,56,0.12)' }}>
          <span className="text-[13px]" style={{ color: '#4d6157' }}>{tr(COPY.newMap, locale)} :</span>
          <div className="w-56 max-w-full"><Field id="cm-new-name" value={name} onChange={setName} placeholder={tr(COPY.newName, locale)} /></div>
          <button type="button" disabled={!name.trim() || busy} className="cm-btn disabled:opacity-50" onClick={() => create(null)}>
            <Plus className="h-3.5 w-3.5" /> {tr(COPY.createBlank, locale)}
          </button>
          <button type="button" disabled={!name.trim() || busy} onClick={() => createRef.current?.click()} className="cm-btn disabled:opacity-50">
            <Upload className="h-3.5 w-3.5" /> {tr(COPY.createXlsx, locale)}
          </button>
          <input ref={createRef} type="file" accept=".xlsx" hidden
                 onChange={e => { const f = e.target.files[0]; e.target.value = ''; if (f) create(f); }} />
        </div>
      )}
      {busy && <div className="text-[13px]" style={{ color: LT.green50 }}>{tr(COPY.busy, locale)}</div>}
      {err && <div className="text-[13px]" style={{ color: '#b23a2a' }}>{err}</div>}
    </div>
  );
}

/* Read or edit one text, depending on the mode. */
function Text({ editing, value, onChange, compact, rows }) {
  return editing ? <Area value={value} onChange={onChange} rows={rows} /> : <CellText text={value} compact={compact} />;
}

/* ── The ladder: levels grouped by stage ──────────────────────────── */

function Ladder({ map, value, onPick, locale }) {
  const count = map.levels.length;
  return (
    <div className="mt-6 overflow-x-auto -mx-1 px-1 pb-1">
      <div className="min-w-[680px]">
        {/* The staircase · each level is a step a little higher than the last. */}
        <div className="grid gap-1.5 items-end" style={{ gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))` }}>
          {map.levels.map((l, i) => {
            const on = l.n === value;
            const tone = toneFor(map, l.n);
            return (
              <button key={l.n} type="button" onClick={() => onPick(l.n)} aria-pressed={on}
                      aria-label={`${tr(COPY.level, locale)} ${l.n} · ${l.name}`}
                      className="relative rounded-t-xl rounded-b-md text-left px-2.5 pt-2 pb-2.5 flex flex-col justify-end transition focus-visible:outline-none focus-visible:ring-2"
                      style={{
                        height: `${64 + i * 14}px`,
                        background: tone.fill,
                        color: tone.ink,
                        boxShadow: on ? `0 0 0 3px ${LT.yellow}, 0 8px 18px rgba(43,65,56,0.18)` : 'none',
                        transform: on ? 'translateY(-4px)' : 'none',
                        '--tw-ring-color': LT.yellow,
                      }}>
                <span className="font-display text-[1.35rem] leading-none font-num">{l.n}</span>
                <span className="text-[12.5px] font-medium leading-tight mt-1">{l.name}</span>
                <span className="font-num text-[10.5px] opacity-75">{l.years}</span>
              </button>
            );
          })}
        </div>
        {/* Stage bands under the steps. */}
        <div className="grid gap-1.5 mt-1.5" style={{ gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))` }}>
          {map.stages.map((s, si) => (
            <div key={si} className="rounded-md px-2.5 py-1.5 min-w-0"
                 style={{ gridColumn: `${s.levels[0]} / span ${s.levels.length}`, background: toneFor(map, s.levels[0]).soft }}>
              <div className="text-[11px] uppercase tracking-[0.14em] font-medium" style={{ color: LT.greenDeep }}>
                {s.key} <span className="normal-case tracking-normal font-normal" style={{ color: '#4d6157' }}>· {s.question}</span>
              </div>
              <div className="text-[12px] truncate" style={{ color: '#4d6157' }} title={s.title}>{s.title}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Path: one level in full ──────────────────────────────────────── */

function PathView({ map, locale, editing, update, text, keywordRow }) {
  const [n, setN] = useState(1);
  const level = map.levels.find(l => l.n === n);
  const li = map.levels.findIndex(l => l.n === n);
  const stage = stageFor(map, n);
  const max = map.levels.length;
  const keyword = keywordRow >= 0 ? cellFor(map.rows[keywordRow], n) : null;

  return (
    <>
      <Ladder map={map} value={n} onPick={setN} locale={locale} />
      <section className="mt-5 rounded-2xl p-5 sm:p-6" style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}` }}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] font-medium" style={{ color: LT.green50 }}>
              <span className="h-2.5 w-2.5 rounded-sm" style={{ background: toneFor(map, n).fill, border: `1px solid ${LT.green30}` }} />
              {stage?.key} · {stage?.title}
            </div>
            {editing ? (
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-[auto_1fr_10rem] gap-2 items-center max-w-[560px]">
                <span className="font-display text-[1.3rem]" style={{ color: LT.greenDeep }}>{tr(COPY.level, locale)} {n}</span>
                <Field value={level?.name} onChange={v => text(d => { d.levels[li].name = v; })} />
                <Field value={level?.years} onChange={v => text(d => { d.levels[li].years = v; })} />
              </div>
            ) : (
              <>
                <h2 className="font-display text-[1.7rem] leading-tight mt-1" style={{ color: LT.greenDeep }}>
                  {tr(COPY.level, locale)} {n} · {level?.name}
                </h2>
                <div className="font-num text-[13px] mt-0.5" style={{ color: LT.green50 }}>{level?.years}</div>
                {keyword && <Tagline text={keyword.text} />}
              </>
            )}
          </div>
          <div className="flex gap-1.5">
            <button type="button" onClick={() => setN(Math.max(1, n - 1))} disabled={n === 1} aria-label="previous"
                    className="h-9 w-9 rounded-lg grid place-items-center disabled:opacity-30" style={{ border: `1px solid ${LT.green30}`, color: LT.greenDeep }}>
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button type="button" onClick={() => setN(Math.min(max, n + 1))} disabled={n === max} aria-label="next"
                    className="h-9 w-9 rounded-lg grid place-items-center disabled:opacity-30" style={{ border: `1px solid ${LT.green30}`, color: LT.greenDeep }}>
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6">
          {map.rows.map((row, ri) => {
            const ci = row.cells.findIndex(c => c.levels.includes(n));
            const cell = row.cells[ci];
            if (!cell && !editing) return null;
            if (ri === keywordRow && !editing) return null;  // shown as the tagline above
            return (
              <div key={ri} className="min-w-0">
                <h3 className="text-[13px] font-medium flex items-center gap-1.5 pb-1.5 mb-2 border-b"
                    style={{ color: LT.greenDeep, borderColor: LT.green30 }}>
                  {row.icon && <span aria-hidden>{row.icon}</span>} {row.label}
                  {cell && cell.levels.length > 1 && (
                    <span className="ml-auto text-[11.5px] font-normal font-num" style={{ color: LT.green50 }}>
                      {tr(COPY.shared, locale)} {cell.levels[0]}–{cell.levels[cell.levels.length - 1]}
                    </span>
                  )}
                </h3>
                <div className="text-[14px] leading-relaxed" style={{ color: '#3a4f44' }}>
                  {cell ? (
                    <Text editing={editing} value={cell.text} onChange={v => text(d => { d.rows[ri].cells[ci].text = v; })} rows={6} />
                  ) : (
                    <button type="button" className="cm-btn" onClick={() => update(d => {
                      d.rows[ri].cells.push({ levels: [n], text: '' });
                      d.rows[ri].cells.sort((a, b) => a.levels[0] - b.levels[0]);
                    })}><Plus className="h-3.5 w-3.5" /> {tr(COPY.addCell, locale)}</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
}

/* ── Compare: two levels side by side ─────────────────────────────── */

function CompareView({ map, canon, locale, keywordRow, initialLevel }) {
  const top = map.levels.length;
  const start = Math.min(Math.max(1, initialLevel || 3), top - 1);
  const [a, setA] = useState(start);
  const [b, setB] = useState(start + 1);
  const pick = (value, set, id) => (
    <select id={id} value={value} onChange={e => set(Number(e.target.value))}
            className="rounded-lg px-3 py-2 text-[14px]" style={{ background: '#fff', border: `1px solid ${LT.green30}`, color: LT.greenDeep }}>
      {map.levels.map(l => <option key={l.n} value={l.n}>{tr(COPY.level, locale)} {l.n} · {l.name}</option>)}
    </select>
  );

  return (
    <section className="mt-5">
      <div className="flex flex-wrap items-center gap-2 text-[14px]" style={{ color: LT.greenDeep }}>
        <label htmlFor="cm-a">{tr(COPY.from, locale)}</label> {pick(a, setA, 'cm-a')}
        <ArrowRight className="h-4 w-4" style={{ color: LT.green50 }} />
        <label htmlFor="cm-b">{tr(COPY.to, locale)}</label> {pick(b, setB, 'cm-b')}
        <button type="button" onClick={() => { setA(b); setB(a); }} className="cm-btn"><Repeat className="h-3.5 w-3.5" /> {tr(COPY.swap, locale)}</button>
        <button type="button" disabled={Math.max(a, b) >= top} onClick={() => { setA(Math.min(a, b) + 1); setB(Math.max(a, b) + 1); }}
                className="cm-btn disabled:opacity-40">{tr(COPY.nextPair, locale)} <ChevronRight className="h-3.5 w-3.5" /></button>
        {stageFor(map, a)?.key !== stageFor(map, b)?.key && (
          <span className="text-[12px] rounded-full px-2.5 py-1 ml-1" style={{ background: LT.yellowLight, color: LT.greenDeep }}>
            {tr(COPY.stageChange, locale)}
          </span>
        )}
      </div>
      <div className="mt-4 mb-2 hidden sm:grid grid-cols-2 gap-4">
        {[a, b].map((n, i) => (
          <div key={i} className="min-w-0">
            <div className="font-display text-[1.2rem]" style={{ color: LT.greenDeep }}>
              {tr(COPY.level, locale)} {n} · {map.levels.find(l => l.n === n)?.name}
            </div>
            <div className="text-[12px] font-num" style={{ color: LT.green50 }}>{stageFor(map, n)?.key} · {map.levels.find(l => l.n === n)?.years}</div>
            {keywordRow >= 0 && <Tagline text={cellFor(map.rows[keywordRow], n)?.text} />}
          </div>
        ))}
      </div>
      <div className="space-y-4 mt-4 sm:mt-0">
        {map.rows.map((row, ri) => {
          const ca = cellFor(row, a);
          const cb = cellFor(row, b);
          if (!ca && !cb) return null;
          const same = ca && cb && ca === cb;
          return (
            <div key={ri} className="rounded-2xl p-4" style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}` }}>
              <h3 className="text-[13.5px] mb-2 flex items-center gap-2" style={{ color: LT.greenDeep }}>
                {(() => { const Icon = rowIcon(canon?.rows?.[ri]?.label); return <Icon className="h-4 w-4" />; })()} {row.label}
              </h3>
              {same ? (
                <div className="text-[13.5px] leading-relaxed" style={{ color: '#3a4f44' }}>
                  <div className="text-[11.5px] mb-1 font-num" style={{ color: LT.green50 }}>{tr(COPY.shared, locale)} {ca.levels[0]}–{ca.levels[ca.levels.length - 1]}</div>
                  <Fold locale={locale}><CellText text={ca.text} compact /></Fold>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-[13.5px] leading-relaxed" style={{ color: '#3a4f44' }}>
                  <div className="min-w-0">
                    <div className="sm:hidden text-[11.5px] font-num mb-1" style={{ color: LT.green50 }}>{tr(COPY.level, locale)} {a}</div>
                    {ca ? <Fold locale={locale}><CellText text={ca.text} compact /></Fold> : '·'}
                  </div>
                  <div className="min-w-0 sm:pl-4 sm:border-l" style={{ borderColor: LT.green30 }}>
                    <div className="sm:hidden text-[11.5px] font-num mb-1" style={{ color: LT.green50 }}>{tr(COPY.level, locale)} {b}</div>
                    {cb ? <Fold locale={locale}><CellText text={cb.text} compact /></Fold> : '·'}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* Long blocks fold to ~10 lines with a fade and a "show more" toggle. */
function Fold({ children, locale }) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [tall, setTall] = useState(false);
  useEffect(() => { if (ref.current) setTall(ref.current.scrollHeight > 260); }, [children]);
  return (
    <div>
      <div ref={ref} className="relative overflow-hidden" style={{ maxHeight: open || !tall ? 'none' : 240 }}>
        {children}
        {tall && !open && <div className="absolute inset-x-0 bottom-0 h-16" style={{ background: `linear-gradient(to bottom, transparent, ${LT.beigeLight})` }} />}
      </div>
      {tall && (
        <button type="button" onClick={() => setOpen(o => !o)} className="mt-1 text-[13px] underline underline-offset-4" style={{ color: LT.greenDeep }}>
          {tr(open ? COPY.showLess : COPY.showMore, locale)}
        </button>
      )}
    </div>
  );
}

/* ── Full grid: the whole map · also the structure editor ─────────── */

function GridView({ map, canon, locale, editing, update, text }) {
  const cols = map.levels.length;
  const colW = editing ? 260 : 220;
  const labelW = editing ? 220 : 180;

  const moveRow = (ri, dir) => update(d => {
    const [r] = d.rows.splice(ri, 1);
    d.rows.splice(ri + dir, 0, r);
  });
  const splitCell = (ri, ci) => update(d => {
    const c = d.rows[ri].cells[ci];
    d.rows[ri].cells.splice(ci, 1, ...c.levels.map(l => ({ levels: [l], text: c.text })));
  });
  const mergeNext = (ri, ci) => update(d => {
    const cells = d.rows[ri].cells;
    const a = cells[ci];
    const b = cells[ci + 1];
    a.levels = [...a.levels, ...b.levels];
    if (b.text && b.text !== a.text) a.text = [a.text, b.text].filter(Boolean).join('\n\n');
    cells.splice(ci + 1, 1);
  });
  // A merge only makes sense between neighbouring levels.
  const canMerge = (row, ci) => row.cells[ci + 1]
    && row.cells[ci + 1].levels[0] === row.cells[ci].levels[row.cells[ci].levels.length - 1] + 1;

  return (
    <div className="mt-5">
      <div className="overflow-x-auto rounded-2xl" style={{ border: `1px solid ${LT.green30}`, background: LT.beigeLight }}>
        <table className="text-[12.5px] leading-snug border-collapse" style={{ minWidth: `${labelW + cols * colW}px`, color: '#3a4f44' }}>
          <thead>
            <tr>
              <th className="sticky left-0 z-10 p-3 text-left" style={{ background: LT.beigeWarm, width: labelW }} />
              {map.stages.map((s, si) => (
                <th key={si} colSpan={s.levels.length} className="p-3 text-left font-medium border-l align-top"
                    style={{ background: toneFor(map, s.levels[0]).soft, color: LT.greenDeep, borderColor: LT.green30 }}>
                  {editing ? (
                    <div className="space-y-1.5 font-normal">
                      <div className="flex gap-1.5">
                        <div className="w-24 shrink-0"><Field value={s.key} onChange={v => text(d => { d.stages[si].key = v; })} /></div>
                        <Field value={s.question} onChange={v => text(d => { d.stages[si].question = v; })} />
                      </div>
                      <Field value={s.title} onChange={v => text(d => { d.stages[si].title = v; })} />
                      <Field value={s.summary} onChange={v => text(d => { d.stages[si].summary = v; })} />
                    </div>
                  ) : (
                    <>
                      {s.key} · {s.question}
                      <div className="font-normal" style={{ color: LT.green50 }}>{s.title}</div>
                    </>
                  )}
                </th>
              ))}
            </tr>
            <tr>
              <th className="sticky left-0 z-10 p-3" style={{ background: LT.beigeLight }} />
              {map.levels.map((l, li) => (
                <th key={l.n} className="p-3 text-left align-top border-l border-t" style={{ borderColor: LT.green30, color: LT.greenDeep }}>
                  {editing ? (
                    <div className="space-y-1.5 font-normal">
                      <div className="font-num text-[11px]" style={{ color: LT.green50 }}>{tr(COPY.level, locale)} {l.n}</div>
                      <Field value={l.name} onChange={v => text(d => { d.levels[li].name = v; })} />
                      <Field value={l.years} onChange={v => text(d => { d.levels[li].years = v; })} />
                    </div>
                  ) : (
                    <>
                      <span className="font-num" style={{ color: LT.green50 }}>{l.n}</span> {l.name}
                      <div className="font-num font-normal" style={{ color: LT.green50 }}>{l.years}</div>
                    </>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {map.rows.map((row, ri) => (
              <tr key={ri}>
                <th className="sticky left-0 z-10 p-3 text-left align-top font-medium border-t"
                    style={{ background: LT.beigeLight, color: LT.greenDeep, borderColor: LT.green30 }}>
                  {editing ? (
                    <div className="space-y-1.5 font-normal">
                      <div className="flex gap-1.5">
                        <div className="w-12 shrink-0"><Field value={row.icon} onChange={v => text(d => { d.rows[ri].icon = v; })} className="text-center" /></div>
                        <Field value={row.label} onChange={v => text(d => { d.rows[ri].label = v; })} placeholder={tr(COPY.rowLabel, locale)} />
                      </div>
                      <div className="flex gap-0.5">
                        <IconBtn label="up" disabled={ri === 0} onClick={() => moveRow(ri, -1)}><ChevronUp className="h-4 w-4" /></IconBtn>
                        <IconBtn label="down" disabled={ri === map.rows.length - 1} onClick={() => moveRow(ri, 1)}><ChevronDown className="h-4 w-4" /></IconBtn>
                        <IconBtn label="delete" danger onClick={() => update(d => { d.rows.splice(ri, 1); })}><Trash2 className="h-4 w-4" /></IconBtn>
                      </div>
                    </div>
                  ) : (
                    <span className="inline-flex items-center gap-1.5">
                      {(() => { const Icon = rowIcon(canon?.rows?.[ri]?.label); return <Icon className="h-3.5 w-3.5 shrink-0" />; })()} {row.label}
                    </span>
                  )}
                </th>
                {row.cells.map((c, ci) => (
                  <td key={c.levels[0]} colSpan={c.levels.length} className="p-3 align-top border-l border-t"
                      style={{ borderColor: LT.green30 }}>
                    <Text editing={editing} value={c.text} compact rows={6}
                          onChange={v => text(d => { d.rows[ri].cells[ci].text = v; })} />
                    {editing && (
                      <div className="flex gap-0.5 mt-1">
                        {c.levels.length > 1 && <IconBtn label={tr(COPY.split, locale)} onClick={() => splitCell(ri, ci)}><Scissors className="h-3.5 w-3.5" /></IconBtn>}
                        {canMerge(row, ci) && <IconBtn label={tr(COPY.mergeNext, locale)} onClick={() => mergeNext(ri, ci)}><Merge className="h-3.5 w-3.5" /></IconBtn>}
                      </div>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && (
        <button type="button" className="cm-btn mt-3"
                onClick={() => update(d => { d.rows.push({ label: '', icon: '', cells: blankCells(d.levels) }); })}>
          <Plus className="h-3.5 w-3.5" /> {tr(COPY.addRow, locale)}
        </button>
      )}
    </div>
  );
}

/* ── Simulator: an evaluation against the promotion rules ─────────── */

function Simulator({ map, canon, locale, editing, update, text, root, initialLevel }) {
  const [level, setLevel] = useState(Math.min(initialLevel || 1, map.levels.length));
  const [scores, setScores] = useState({});
  const rules = map.promotion;
  const groups = useMemo(() => map.evaluation?.groups || [], [map]);

  // Scores are keyed by position, and Leadership is recognised on the
  // French name, so both work whatever language the page is in.
  const items = useMemo(() => groups.flatMap((g, gi) => g.items.map((it, ii) => ({
    ...it, key: `${gi}.${ii}`, optional: !!g.optional,
    hidden: /leadership/i.test(canon?.evaluation?.groups?.[gi]?.items?.[ii]?.name || '') && level < (rules?.leadership_from || 4),
  }))), [groups, canon, level, rules]);

  if (editing) return <SimulatorEditor map={map} locale={locale} update={update} text={text} root={root} />;

  const required = items.filter(i => !i.hidden && !i.optional);
  const counted = items.filter(i => !i.hidden && scores[i.key] != null);
  const allScored = required.length > 0 && required.every(i => scores[i.key] != null);
  const avg = counted.length ? counted.reduce((s, i) => s + scores[i.key], 0) / counted.length : null;
  const min = counted.length ? Math.min(...counted.map(i => scores[i.key])) : null;
  const step = rules?.steps?.[String(level)];
  const alert = rules && avg != null && (avg <= rules.alert_average || min <= rules.alert_score);
  const top = map.levels[map.levels.length - 1]?.n;

  const checks = step && allScored ? [
    { ok: avg >= step.average, text: `${tr(COPY.avgNeed, locale)} ${String(step.average).replace('.', ',')} ${step.evaluations > 1
        ? tr(COPY.evalsN, locale, { n: step.evaluations, m: step.evaluations * rules.months_per_evaluation })
        : tr(COPY.evals1, locale)}` },
    { ok: min >= rules.floor, text: tr(COPY.floorOk, locale, { f: rules.floor }) },
  ] : [];

  return (
    <section className="mt-5 grid grid-cols-1 lg:grid-cols-12 gap-5">
      <div className="lg:col-span-7 space-y-4">
        <p className="text-[14px] leading-relaxed" style={{ color: '#3a4f44' }}>{tr(COPY.simIntro, locale)}</p>
        <div className="flex items-center gap-2 flex-wrap">
          <label htmlFor="cm-level" className="text-[14px]" style={{ color: LT.greenDeep }}>{tr(COPY.current, locale)}</label>
          <select id="cm-level" value={level} onChange={e => setLevel(Number(e.target.value))}
                  className="rounded-lg px-3 py-2 text-[14px]" style={{ background: '#fff', border: `1px solid ${LT.green30}`, color: LT.greenDeep }}>
            {map.levels.map(l => <option key={l.n} value={l.n}>{tr(COPY.level, locale)} {l.n} · {l.name}</option>)}
          </select>
        </div>
        {groups.map((g, gi) => {
          const visible = items.filter(i => i.key.startsWith(`${gi}.`) && !i.hidden);
          if (!visible.length) return null;
          return (
            <div key={gi} className="rounded-2xl p-4" style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}` }}>
              <div className="text-[12px] uppercase tracking-[0.1em] font-medium mb-2" style={{ color: LT.green50 }}>
                {g.icon} {g.title}{g.optional && <span className="normal-case tracking-normal"> · {tr(COPY.optionalGrp, locale)}</span>}
              </div>
              <div className="space-y-3">
                {visible.map(it => (
                  <div key={it.key} role="radiogroup" aria-label={it.name} className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-[14.5px] font-medium" style={{ color: LT.greenDeep }}>{it.name}</div>
                      {it.hint && <div className="text-[12.5px]" style={{ color: LT.green50 }}>{it.hint}</div>}
                    </div>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map(v => {
                        const on = scores[it.key] === v;
                        return (
                          <button key={v} type="button" role="radio" aria-checked={on}
                                  onClick={() => setScores(s => ({ ...s, [it.key]: on ? undefined : v }))}
                                  title={map.scale.find(x => x.score === v)?.text}
                                  className="h-9 w-9 rounded-full text-[14px] font-num transition"
                                  style={on
                                    ? { background: v < (rules?.floor || 3) ? '#b23a2a' : LT.greenDeep, color: '#fff', border: '1px solid transparent' }
                                    : { background: '#fff', color: LT.greenDeep, border: `1px solid ${LT.green30}` }}>
                            {v}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <aside className="lg:col-span-5">
        <div className="lg:sticky lg:top-24 rounded-2xl p-5 space-y-4" style={{ background: LT.greenDeep, color: LT.beigeLight }}>
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] opacity-70">{tr(COPY.average, locale)}</div>
            <div className="font-display text-[3rem] leading-none mt-1 font-num">
              {avg == null ? '–' : avg.toFixed(2).replace('.', ',')}
            </div>
            <div className="text-[12.5px] opacity-70 mt-1 font-num">{counted.length} {tr(COPY.scored, locale)}</div>
            <ScoreTrack avg={avg} floor={rules?.floor} need={level < top ? step?.average : null} locale={locale} />
          </div>

          {level >= top ? (
            <p className="text-[14px]">{tr(COPY.terminal, locale)}</p>
          ) : step && (
            <div className="space-y-2">
              <div className="text-[14px] font-medium">
                {tr(COPY.toNext, locale)} {level + 1}
                {step.stage_change && <span className="ml-2 text-[11.5px] rounded-full px-2 py-0.5" style={{ background: LT.yellow, color: LT.greenDeep }}>{tr(COPY.stageChange, locale)}</span>}
              </div>
              {!allScored && <p className="text-[13px] opacity-80">{tr(COPY.scoreAll, locale)}</p>}
              {checks.map(c => (
                <div key={c.text} className="flex items-start gap-2 text-[13.5px]">
                  {c.ok ? <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" style={{ color: LT.yellow }} /> : <CircleDashed className="h-4 w-4 shrink-0 mt-0.5 opacity-70" />}
                  <span>{c.text} · <b>{tr(c.ok ? COPY.met : COPY.notMet, locale)}</b></span>
                </div>
              ))}
              <div className="flex items-start gap-2 text-[13.5px] opacity-90">
                <CircleDashed className="h-4 w-4 shrink-0 mt-0.5 opacity-70" />
                <span>{tr(COPY.approval[step.approval], locale)}</span>
              </div>
            </div>
          )}

          {alert && (
            <div className="flex items-start gap-2 text-[13px] rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.1)' }}>
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" style={{ color: LT.yellow }} />
              {tr(COPY.alert, locale, { a: rules.alert_average, s: rules.alert_score })}
            </div>
          )}
        </div>
        {(() => {
          const ri = rowIndex(canon, NEXT_RE);
          const cell = ri >= 0 ? cellFor(map.rows[ri], level) : null;
          return cell && level < top && (
            <div className="mt-3 p-4 text-[13.5px] leading-relaxed" style={{ background: LT.beigeWarm, color: '#3a4f44' }}>
              <div className="text-[11px] uppercase tracking-[0.12em] mb-1.5" style={{ color: LT.green50 }}>{tr(COPY.expected, locale)}</div>
              <CellText text={cell.text} compact />
            </div>
          );
        })()}
        {map.evaluation?.tip && (
          <p className="text-[13px] mt-3 leading-relaxed" style={{ color: '#4d6157' }}>
            <b style={{ color: LT.greenDeep }}>{tr(COPY.tip, locale)} ·</b> {map.evaluation.tip}
          </p>
        )}
      </aside>
    </section>
  );
}

/* The average on a 1–5 track, with the floor and the average the next
 * level needs marked on it · the headline number in context. */
function ScoreTrack({ avg, floor, need, locale }) {
  const x = (v) => `${((v - 1) / 4) * 100}%`;
  return (
    <div className="mt-4" aria-hidden>
      <div className="relative h-2.5 rounded-full" style={{ background: 'rgba(255,255,255,0.16)' }}>
        {avg != null && (
          <div className="absolute left-0 top-0 h-full rounded-full" style={{ width: x(avg), background: LT.yellow }} />
        )}
        {floor && <span className="absolute -top-1 h-[18px] w-0.5 rounded" style={{ left: x(floor), background: 'rgba(255,255,255,0.55)' }} />}
        {need && <span className="absolute -top-1.5 h-[22px] w-0.5 rounded" style={{ left: x(need), background: '#fff' }} />}
      </div>
      <div className="relative h-8 mt-1 text-[11px] font-num">
        {[1, 2, 3, 4, 5].map(v => (
          <span key={v} className="absolute -translate-x-1/2 opacity-60" style={{ left: x(v) }}>{v}</span>
        ))}
        {need && (
          <span className="absolute top-3.5 -translate-x-1/2 whitespace-nowrap" style={{ left: x(need) }}>
            {tr(COPY.avgNeed, locale)} {String(need).replace('.', ',')}
          </span>
        )}
      </div>
    </div>
  );
}

/* In edit mode the simulator tab edits what drives it: the review form
 * (groups and criteria) and the promotion thresholds per move. */
function SimulatorEditor({ map, locale, update, text, root }) {
  const p = map.promotion || {};
  const groups = map.evaluation?.groups || [];
  const num = (v) => {
    const n = Number(String(v).replace(',', '.'));
    return v === '' || Number.isNaN(n) ? v : n;
  };
  const ensureEval = (d) => { if (!d.evaluation) d.evaluation = { groups: [], tip: '' }; return d.evaluation; };

  return (
    <section className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="min-w-0">
        <h2 className="font-display text-[1.35rem]" style={{ color: LT.greenDeep }}>{tr(COPY.evalForm, locale)}</h2>
        <div className="mt-3 space-y-3">
          {groups.map((g, gi) => (
            <div key={gi} className="rounded-2xl p-3 space-y-2" style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}` }}>
              <div className="flex gap-1.5 items-center">
                <div className="w-12 shrink-0"><Field value={g.icon} onChange={v => text(d => { d.evaluation.groups[gi].icon = v; })} className="text-center" /></div>
                <Field value={g.title} onChange={v => text(d => { d.evaluation.groups[gi].title = v; })} />
                <label className="text-[12px] inline-flex items-center gap-1 whitespace-nowrap" style={{ color: LT.green50 }}>
                  <input type="checkbox" checked={!!g.optional} onChange={e => update(d => { d.evaluation.groups[gi].optional = e.target.checked; })} />
                  {tr(COPY.optionalGrp, locale)}
                </label>
                <IconBtn label="delete" danger onClick={() => update(d => { d.evaluation.groups.splice(gi, 1); })}><Trash2 className="h-4 w-4" /></IconBtn>
              </div>
              {g.items.map((it, ii) => (
                <div key={ii} className="flex gap-1.5 items-center pl-4">
                  <Field value={it.name} onChange={v => text(d => { d.evaluation.groups[gi].items[ii].name = v; })} />
                  <Field value={it.hint} onChange={v => text(d => { d.evaluation.groups[gi].items[ii].hint = v; })} />
                  <IconBtn label="delete" danger onClick={() => update(d => { d.evaluation.groups[gi].items.splice(ii, 1); })}><Trash2 className="h-3.5 w-3.5" /></IconBtn>
                </div>
              ))}
              <button type="button" className="cm-btn ml-4" onClick={() => update(d => { d.evaluation.groups[gi].items.push({ name: '', icon: '', hint: '' }); })}>
                <Plus className="h-3.5 w-3.5" /> {tr(COPY.addItem, locale)}
              </button>
            </div>
          ))}
          <button type="button" className="cm-btn" onClick={() => update(d => { ensureEval(d).groups.push({ title: '', icon: '', items: [] }); })}>
            <Plus className="h-3.5 w-3.5" /> {tr(COPY.addGroup, locale)}
          </button>
          <div>
            <label className="text-[12px]" style={{ color: LT.green50 }}>{tr(COPY.tip, locale)}</label>
            <Area value={map.evaluation?.tip} onChange={v => text(d => { ensureEval(d).tip = v; })} rows={2} />
          </div>
        </div>
      </div>

      <div className="min-w-0">
        <h2 className="font-display text-[1.35rem]" style={{ color: LT.greenDeep }}>{tr(COPY.thresholds, locale)}</h2>
        <div className="mt-3 overflow-x-auto rounded-2xl" style={{ border: `1px solid ${LT.green30}`, background: LT.beigeLight }}>
          <table className="w-full text-[13px]" style={{ color: LT.greenDeep }}>
            <thead>
              <tr className="text-left text-[11.5px] uppercase tracking-[0.08em]" style={{ color: LT.green50 }}>
                <th className="p-2.5 font-medium">{tr(COPY.step, locale)}</th>
                <th className="p-2.5 font-medium">{tr(COPY.avgNeed, locale)}</th>
                <th className="p-2.5 font-medium">{tr(COPY.evaluations, locale)}</th>
                <th className="p-2.5 font-medium">{tr(COPY.approvalLbl, locale)}</th>
                <th className="p-2.5 font-medium">{tr(COPY.stageChange, locale)}</th>
              </tr>
            </thead>
            <tbody>
              {map.levels.slice(0, -1).map(l => {
                const k = String(l.n);
                const s = p.steps?.[k] || { average: 3.5, evaluations: 1, approval: 'manager' };
                const set = (field, v) => root(d => {
                  d.promotion = d.promotion || {};
                  d.promotion.steps = d.promotion.steps || {};
                  d.promotion.steps[k] = { ...s, ...(d.promotion.steps[k] || {}), [field]: v };
                });
                return (
                  <tr key={k} className="border-t" style={{ borderColor: LT.green30 }}>
                    <td className="p-2.5 font-num whitespace-nowrap">{l.n} → {l.n + 1}</td>
                    <td className="p-2.5"><div className="w-16"><Field value={String(s.average)} onChange={v => set('average', num(v))} /></div></td>
                    <td className="p-2.5"><div className="w-14"><Field value={String(s.evaluations)} onChange={v => set('evaluations', num(v))} /></div></td>
                    <td className="p-2.5">
                      <select value={s.approval} onChange={e => set('approval', e.target.value)} className={inputCls} style={inputStyle}>
                        {Object.keys(COPY.approval).map(a => <option key={a} value={a}>{tr(COPY.approval[a], locale)}</option>)}
                      </select>
                    </td>
                    <td className="p-2.5 text-center"><input type="checkbox" checked={!!s.stage_change} onChange={e => set('stage_change', e.target.checked)} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-[13px]" style={{ color: LT.greenDeep }}>
          {[['floor', COPY.floorLbl], ['alert_average', COPY.alertAvg], ['alert_score', COPY.alertScore],
            ['leadership_from', COPY.leaderFrom], ['months_per_evaluation', COPY.months]].map(([key, label]) => (
            <label key={key} className="flex items-center justify-between gap-2 rounded-lg px-3 py-2" style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}` }}>
              <span>{tr(label, locale)}</span>
              <div className="w-16 shrink-0"><Field value={String(p[key] ?? '')} onChange={v => root(d => { d.promotion = { ...(d.promotion || {}), [key]: num(v) }; })} /></div>
            </label>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Reference: rules, scale, competencies ────────────────────────── */

function Reference({ map, canon, locale, editing, update, text }) {
  return (
    <section className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="lg:col-span-1 min-w-0">
        <h2 className="font-display text-[1.35rem]" style={{ color: LT.greenDeep }}>{tr(COPY.rules, locale)}</h2>
        <div className="mt-3 space-y-4 text-[13.5px] leading-relaxed" style={{ color: '#3a4f44' }}>
          {map.rules.map((r, i) => (
            <div key={i}>
              {editing ? (
                <div className="flex gap-1.5 mb-1.5">
                  <Field value={r.label} onChange={v => text(d => { d.rules[i].label = v; })} />
                  <IconBtn label="delete" danger onClick={() => update(d => { d.rules.splice(i, 1); })}><Trash2 className="h-4 w-4" /></IconBtn>
                </div>
              ) : (
                <h3 className="text-[12px] uppercase tracking-[0.1em] font-medium mb-1" style={{ color: LT.green50 }}>{r.label}</h3>
              )}
              <Text editing={editing} value={r.text} compact rows={4} onChange={v => text(d => { d.rules[i].text = v; })} />
            </div>
          ))}
          {editing && (
            <button type="button" className="cm-btn" onClick={() => update(d => { d.rules.push({ label: '', text: '' }); })}>
              <Plus className="h-3.5 w-3.5" /> {tr(COPY.addRule, locale)}
            </button>
          )}
          {editing ? (
            <div>
              <label className="text-[12px]" style={{ color: LT.green50 }}>{tr(COPY.floorRule, locale)}</label>
              <Area value={map.floor_rule} onChange={v => text(d => { d.floor_rule = v; })} rows={3} />
            </div>
          ) : map.floor_rule && (
            <p className="rounded-xl p-3 text-[13px]" style={{ background: LT.yellowLight, color: LT.greenDeep }}>⚠️ {map.floor_rule}</p>
          )}
        </div>
      </div>
      <div className="lg:col-span-1 min-w-0">
        <h2 className="font-display text-[1.35rem]" style={{ color: LT.greenDeep }}>{tr(COPY.scale, locale)}</h2>
        {editing ? (
          <div className="mt-2">
            <label className="text-[12px]" style={{ color: LT.green50 }}>{tr(COPY.scaleNote, locale)}</label>
            <Area value={map.scale_note} onChange={v => text(d => { d.scale_note = v; })} rows={2} />
          </div>
        ) : map.scale_note && <p className="text-[13px] mt-2 leading-relaxed" style={{ color: '#4d6157' }}>{map.scale_note}</p>}
        <div className="mt-3 flex h-2.5 rounded-full overflow-hidden gap-0.5" aria-hidden>
          {map.scale.map(s => (
            <span key={s.score} className="flex-1" style={{ background: s.score < 3 ? '#E7B9AE' : s.score === 3 ? '#D9DED9' : STAGE_TONES[Math.min(3, s.score - 2)].fill }} />
          ))}
        </div>
        <ol className="mt-3 space-y-2 list-none p-0">
          {map.scale.map((s, i) => (
            <li key={s.score} className="flex gap-3 text-[13.5px] leading-snug" style={{ color: '#3a4f44' }}>
              <span className="h-7 w-7 shrink-0 rounded-full grid place-items-center font-num text-[13px]"
                    style={{ background: s.score < 3 ? '#f3dcd7' : s.score === 3 ? LT.beigeWarm : LT.greenSoft, color: LT.greenDeep }}>{s.score}</span>
              {editing ? (
                <div className="flex-1 min-w-0"><Area value={s.text} onChange={v => text(d => { d.scale[i].text = v; })} rows={2} /></div>
              ) : <span>{s.text}</span>}
            </li>
          ))}
        </ol>
      </div>
      <div className="lg:col-span-1 min-w-0">
        <h2 className="font-display text-[1.35rem]" style={{ color: LT.greenDeep }}>{tr(COPY.comps, locale)}</h2>
        <div className="mt-3 space-y-1">
          {map.competencies.map((c, i) => editing ? (
            <div key={i} className="rounded-xl p-2.5 space-y-1.5" style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}` }}>
              <div className="flex gap-1.5">
                <Field value={c.name} onChange={v => text(d => { d.competencies[i].name = v; })} />
                <IconBtn label="delete" danger onClick={() => update(d => { d.competencies.splice(i, 1); })}><Trash2 className="h-4 w-4" /></IconBtn>
              </div>
              <Area value={c.text} onChange={v => text(d => { d.competencies[i].text = v; })} rows={3} />
            </div>
          ) : (
            <Competency key={i} comp={c} Icon={compIcon(canon?.competencies?.[i]?.name)} />
          ))}
          {editing && (
            <button type="button" className="cm-btn mt-1" onClick={() => update(d => { d.competencies.push({ name: '', text: '' }); })}>
              <Plus className="h-3.5 w-3.5" /> {tr(COPY.addComp, locale)}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
